import { useConfig } from '@/context/ConfigContext';
import { configService } from '@/services/ConfigService';

/**
 * Utility hook for easy access to common configuration values - completely dynamic
 * This hook provides a simplified interface to access configuration from all three sources
 */
export const useAppConfig = () => {
  const { config, isLoading, error, updateConfig, reloadConfig } = useConfig();

  return {
    // Core config access
    config,
    isLoading,
    error,
    updateConfig,
    reloadConfig,

    // App Configuration (Metadata & Pages)
    appName: config.metadata.name,
    pages: config.pages,
    showNavigation: true, // Default to true, can be configured via dynamic config

    // Static Configuration (Layout)
    layout: config.layout,

    // Dynamic Configuration - completely dynamic
    ...config, // Spread all dynamic properties

    // Generic helpers
    getConfigValue: (path: string) => configService.getConfigValue(path),
    isFeatureEnabled: (path: string) => configService.isFeatureEnabled(path),
    getSettingsValue: (key: string) => configService.getSettingValue(key),
    isSettingEnabled: (key: string) => configService.isSettingEnabled(key),

    // Settings Panel Helpers - completely dynamic
    getVisibleTabs: () => configService.getVisibleTabs(),
    getTabSettings: (tabName: string) => configService.getTabSettings(tabName),
    getTabLabels: () => configService.getTabLabels(),
    getTabLabel: (tabName: string) => configService.getTabLabel(tabName),

    // Generic setting checker - completely dynamic
    isSettingSelected: (key: string) => {
      const valueMap = configService.checkAndMapStorageData();
      return valueMap[key] ?? false;
    },

    // Get all settings as a map
    getAllSettings: () => configService.checkAndMapStorageData(),

    // Page Configuration Helpers
    getPageConfig: (pageRoute: string) => configService.getPageConfig(pageRoute),
    getPageRoutes: () => configService.getPageRoutes(),
    hasPageRoute: (pageRoute: string) => configService.hasPageRoute(pageRoute),
    getPageMetadata: (pageRoute: string) => configService.getPageMetadata(pageRoute),
    getPageAgentLabel: (pageRoute: string) => configService.getPageAgentLabel(pageRoute),
    getPageInfo: (pageRoute: string) => configService.getPageInfo(pageRoute),
  };
}; 